import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Avatar,
  Typography,
  Chip,
  useTheme,
  Button,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { ArrowBack, Public, Lock, Group, Delete } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import moment from 'moment';
import DOMPurify from 'dompurify';
import { Feed, JobDetails, JobSeekerDetails } from '@/constants/types';
import { feedService } from '@/services/feed/feedService';
import { FeedActions } from '@/components/dashboard/feed/FeedActions';
import { CommentList } from '@/components/dashboard/feed/comments';
import { JobDetailsCard } from '@/components/dashboard/feed/JobDetailsCard';
import { JobSeekerCard } from '@/components/dashboard/feed/JobSeekerCard';
import { PollCard } from '@/components/dashboard/feed/PollCard';
import FeedMedia from '@/components/dashboard/feed/FeedMedia';
import { MediaPreviewModals } from '@/components/dashboard/feed/MediaPreviewModals';
import { useAppDispatch, useAppSelector } from '@/store';
import { followUser, unfollowUser, cancelFollowRequest, deleteFeed } from '@/store';
import { USER_ROLES } from '@/constants/app';

export const FeedViewPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const feedId = searchParams.get('feedId');
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector((state) => state.auth.user);

  const [feed, setFeed] = useState<Feed | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [imagePreview, setImagePreview] = useState({ open: false, url: '', alt: '' });
  const [documentPreview, setDocumentPreview] = useState({ open: false, url: '', fileName: '' });
  const [showComments, setShowComments] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const [localIsFollowing, setLocalIsFollowing] = useState(false);
  const [localRequestPending, setLocalRequestPending] = useState(false);

  useEffect(() => {
    if (!feedId) return;

    const fetchFeed = async () => {
      try {
        setLoading(true);
        const response = await feedService.getFeedDetails(feedId);
        setFeed(response.data.feed);
        setLocalIsFollowing(response.data.feed.relationshipStatus?.isFollowing || false);
        setLocalRequestPending(response.data.feed.relationshipStatus?.isRequestSent || false);
      } catch (err) {
        setError('Failed to load this post.');
      } finally {
        setLoading(false);
      }
    };

    fetchFeed();
  }, [feedId]);

  if (!feedId) {
    navigate('/talky');
    return null;
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !feed) {
    return (
      <Box sx={{ maxWidth: 680, mx: 'auto', pt: 4 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/talky')} sx={{ mb: 2, textTransform: 'none' }}>
          Back
        </Button>
        <Typography color="error">{error || 'Post not found.'}</Typography>
      </Box>
    );
  }

  const isOwnFeed = currentUser?.id === feed.userId._id;
  const isAdmin = currentUser?.role === USER_ROLES.SUPER_ADMIN || currentUser?.role === USER_ROLES.ADMINISTRATOR;

  const handleFollow = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (localIsFollowing || feed.relationshipStatus?.isFollowing) {
      setLocalIsFollowing(false);
      await dispatch(unfollowUser(feed.userId._id));
    } else {
      setLocalRequestPending(true);
      await dispatch(followUser({ recipientId: feed.userId._id }));
    }
  };

  const handleCancelRequest = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setLocalRequestPending(false);
    await dispatch(cancelFollowRequest({ recipientId: feed.userId._id }));
  };

  const handleDeleteConfirm = async () => {
    try {
      await dispatch(deleteFeed(feed._id)).unwrap();
      setDeleteDialogOpen(false);
      navigate('/talky');
    } catch (err) {
      console.error('Failed to delete feed:', err);
    }
  };

  const getVisibilityIcon = (visibility: string) => {
    switch (visibility) {
      case 'public': return <Public fontSize="small" />;
      case 'private': return <Lock fontSize="small" />;
      case 'connections': return <Group fontSize="small" />;
      default: return <Public fontSize="small" />;
    }
  };

  const formatTime = (dateString: string) => {
    try {
      const diff = moment().diff(moment(dateString), 'seconds');
      const m = moment(dateString).local();
      if (diff < 60 && diff >= 0) return `${diff} secs ago`;
      return m.format('DD-MM-YYYY hh:mm A');
    } catch {
      return 'Unknown time';
    }
  };

  const sanitizedContent = DOMPurify.sanitize(feed.content, {
    ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 's', 'del', 'strike', 'a', 'ul', 'ol', 'li', 'span', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'code'],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'style'],
  });

  const handleDocumentClick = (url: string, fileName: string) => {
    const encodedUrl = encodeURIComponent(url);
    const viewerUrl = `https://docs.google.com/viewer?url=${encodedUrl}&embedded=true`;
    setDocumentPreview({ open: true, url: viewerUrl, fileName });
  };

  return (
    <Box sx={{  mx: 'auto' }}>
      {/* Back button */}
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate('/talky')}
        sx={{ mb: 2, textTransform: 'none', color: 'text.secondary' }}
      >
        Back
      </Button>

      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          border: `1px solid ${theme.palette.divider}`,
          overflow: 'hidden',
          boxShadow: 'rgba(0, 0, 0, 0.07) 0px 2px 15px !important',
        }}
      >
        <Box sx={{ p: 2, pb: 1 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Box sx={{ position: 'relative', mr: 2 }}>
              {feed.userId.isLookingForJob ? (
                <Box sx={{ position: 'relative', display: 'inline-block', cursor: 'pointer' }} onClick={() => navigate('/profile/' + feed.userId._id)}>
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -4, left: -4, right: -4, bottom: -4,
                      borderRadius: '50%',
                      background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
                      padding: '4px',
                    }}
                  >
                    <Box sx={{ width: '100%', height: '100%', borderRadius: '50%', background: theme.palette.background.paper }} />
                  </Box>
                  <Avatar
                    src={feed.userId.profilePicture?.url}
                    sx={{ bgcolor: 'primary.main', width: 48, height: 48, position: 'relative', zIndex: 1 }}
                  >
                    {feed.userId.name?.charAt(0)?.toUpperCase() || 'U'}
                  </Avatar>
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: -8, left: '50%', transform: 'translateX(-50%)',
                      background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
                      color: 'white', fontSize: '7px', fontWeight: 700,
                      padding: '2px 8px', borderRadius: '10px', whiteSpace: 'nowrap',
                      zIndex: 2, boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    }}
                  >
                    OPEN TO WORK
                  </Box>
                </Box>
              ) : (
                <Avatar
                  src={feed.userId.profilePicture?.url}
                  onClick={() => navigate('/profile/' + feed.userId._id)}
                  sx={{ bgcolor: 'primary.main', width: 48, height: 48, cursor: 'pointer' }}
                >
                  {feed.userId.name?.charAt(0) || 'U'}
                </Avatar>
              )}
            </Box>

            <Box sx={{ flex: 1 }} onClick={() => navigate('/profile/' + feed.userId._id)}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{feed.userId.name}</Typography>
                {getVisibilityIcon(feed.visibility)}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" color="text.secondary">{feed.userId.designation}</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" color="text.secondary">{formatTime(feed.updatedAt)}</Typography>
                {feed.feedType && (
                  <Chip
                    label={feed.feedType.name.toLowerCase() === 'jobseeker' ? 'Hire me | Open to Work' : feed.feedType.name}
                    size="small" color="primary" variant="outlined"
                    sx={{ fontSize: '0.7rem', height: 20, textTransform: 'capitalize' }}
                  />
                )}
              </Box>
            </Box>

            {/* Follow / Delete buttons */}
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {isAdmin && (
                <IconButton onClick={() => setDeleteDialogOpen(true)} size="small" sx={{ color: 'error.main' }}>
                  <Delete fontSize="small" />
                </IconButton>
              )}
              {!isOwnFeed && feed.relationshipStatus && (
                <>
                  {localIsFollowing || feed.relationshipStatus.isFollowing ? (
                    <Button variant="outlined" size="small" onClick={handleFollow} sx={{ minWidth: 80, fontSize: '0.75rem', textTransform: 'none' }}>
                      Unfollow
                    </Button>
                  ) : localRequestPending || feed.relationshipStatus.isRequestSent ? (
                    <Button
                      variant="outlined" size="small" onClick={handleCancelRequest}
                      sx={{ minWidth: 80, fontSize: '0.75rem', textTransform: 'none', borderColor: 'error.main', color: 'error.main', '&:hover': { borderColor: 'error.dark', bgcolor: 'error.light' } }}
                    >
                      Cancel Request
                    </Button>
                  ) : (
                    <Button
                      size="small" onClick={handleFollow}
                      sx={{ bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' } }}
                    >
                      Follow
                    </Button>
                  )}
                </>
              )}
            </Box>
          </Box>

          {/* Title */}
          {feed.title && (
            <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>{feed.title}</Typography>
          )}

          {/* Poll */}
          {feed.poll && (
            <PollCard
              poll={feed.poll}
              feedId={feed._id}
              userHasVoted={feed.userHasVoted}
              userVoteIndex={feed.userVoteIndex}
            />
          )}

          {/* Content - fully expanded */}
          <Box
            sx={{
              mb: 1, lineHeight: 1.6,
              '& p': { margin: '0.5em 0', fontSize: '1rem' },
              '& h1': { fontSize: '2em', fontWeight: 600, margin: '0.67em 0' },
              '& h2': { fontSize: '1.5em', fontWeight: 600, margin: '0.75em 0' },
              '& h3': { fontSize: '1.17em', fontWeight: 600, margin: '0.83em 0' },
              '& h4': { fontSize: '1em', fontWeight: 600, margin: '1em 0' },
              '& h5': { fontSize: '0.83em', fontWeight: 600, margin: '1.5em 0' },
              '& h6': { fontSize: '0.67em', fontWeight: 600, margin: '2em 0' },
              '& del, & strike': { textDecoration: 'line-through' },
              '& blockquote': { borderLeft: '3px solid #ddd', paddingLeft: '10px', color: '#666', fontStyle: 'italic', margin: '10px 0' },
              '& code': { backgroundColor: '#f5f5f5', padding: '2px 4px', borderRadius: '3px', fontFamily: 'monospace', fontSize: '0.9em' },
              '& a': { color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } },
              '& ul, & ol': { paddingLeft: '1.5em', margin: '0.5em 0' },
              '& li': { margin: '0.25em 0' },
            }}
            dangerouslySetInnerHTML={{ __html: sanitizedContent }}
          />

          {/* Job Details Card */}
          {feed.feedType?.name === 'job' && (feed.feedSpecificDetails || feed.feedSpecificData) && (
            <JobDetailsCard
              jobDetails={(feed.feedSpecificDetails || feed.feedSpecificData) as JobDetails}
              feedUserId={feed.userId._id}
              feed={feed}
              defaultExpanded
            />
          )}

          {/* Job Seeker Card */}
          {feed.feedType?.name === 'jobSeeker' && (feed.feedSpecificDetails || feed.feedSpecificData) && (
            <JobSeekerCard
              jobSeekerDetails={(feed.feedSpecificDetails || feed.feedSpecificData) as JobSeekerDetails}
              feedUserId={feed.userId._id}
              feed={feed}
              defaultExpanded
            />
          )}

          {/* Tags */}
          {feed.tags && feed.tags.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1, mt: 1 }}>
              {feed.tags.map((tag, index) => (
                <Chip key={index} label={`#${tag}`} size="small" color="primary" variant="outlined" sx={{ fontSize: '0.7rem', height: 20 }} />
              ))}
            </Box>
          )}

          {/* Media */}
          <FeedMedia
            media={feed.media}
            onImageClick={(url, alt) => setImagePreview({ open: true, url, alt })}
            onDocumentClick={handleDocumentClick}
          />

          {/* Link Preview */}
          {feed.linkPreview && (
            <Box sx={{ mt: 2, p: 2, border: `1px solid ${theme.palette.divider}`, borderRadius: 2, bgcolor: 'grey.50' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>{feed.linkPreview.title}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{feed.linkPreview.description}</Typography>
              <Typography variant="caption" color="primary">{feed.linkPreview.url}</Typography>
            </Box>
          )}
        </Box>

        {/* Actions (like, comment, share) */}
        <FeedActions feed={feed} onCommentClick={() => setShowComments(prev => !prev)} />

        {/* Comments - open by default on view page */}
        <CommentList feedId={feed._id} showComments={showComments} />
      </Paper>

      {/* Media Preview Modals */}
      <MediaPreviewModals
        imagePreview={imagePreview}
        documentPreview={documentPreview}
        onCloseImagePreview={() => setImagePreview({ open: false, url: '', alt: '' })}
        onCloseDocumentPreview={() => setDocumentPreview({ open: false, url: '', fileName: '' })}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} aria-labelledby="delete-dialog-title">
        <DialogTitle id="delete-dialog-title">Delete Post</DialogTitle>
        <DialogContent>
          <DialogContentText>Are you sure you want to delete this post? This action cannot be undone.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} color="primary">Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" autoFocus>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
